import React, { useEffect, useRef } from 'react';
import jsQR from 'jsqr';

interface ScannerCameraProps {
  onScan: (code: string) => void;
  /** True while a code is being checked: frames are skipped meanwhile. */
  busyRef: React.MutableRefObject<boolean>;
  cameraState: 'off' | 'starting' | 'on' | 'error';
  setCameraState: (state: 'off' | 'starting' | 'on' | 'error') => void;
}

export function ScannerCamera({
  onScan,
  busyRef,
  cameraState,
  setCameraState,
}: ScannerCameraProps): JSX.Element {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraWanted = cameraState === 'starting' || cameraState === 'on';
  const scanRef = useRef(onScan);

  scanRef.current = onScan;

  // Camera loop: one frame every ~150 ms through jsQR
  useEffect(() => {
    if (!cameraWanted) return;
    let stream: MediaStream | null = null;
    let timer = 0;
    let stopped = false;

    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 } },
          audio: false,
        });
        const video = videoRef.current;
        const canvas = canvasRef.current;
        if (!video || !canvas) return;
        video.srcObject = stream;
        await video.play();
        setCameraState('on');
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) return;

        const tick = (): void => {
          if (stopped) return;
          if (video.readyState >= 2 && !busyRef.current) {
            const w = 640;
            const h =
              Math.round((video.videoHeight / video.videoWidth) * w) || 480;
            canvas.width = w;
            canvas.height = h;
            ctx.drawImage(video, 0, 0, w, h);
            const found = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, {
              inversionAttempts: 'dontInvert',
            });
            if (found?.data) scanRef.current(found.data);
          }
          timer = window.setTimeout(tick, 150);
        };
        tick();
      } catch {
        setCameraState('error');
      }
    })();

    return () => {
      stopped = true;
      window.clearTimeout(timer);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [cameraWanted, setCameraState]);

  // Styles
  const viewStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
    aspectRatio: '3 / 4',
    background: '#000',
    borderRadius: 16,
    overflow: 'hidden',
  };

  const videoStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: cameraState === 'on' ? 'block' : 'none',
  };

  return (
    <div style={viewStyle}>
      <video ref={videoRef} playsInline muted style={videoStyle} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* Scanner overlay */}
      {cameraState === 'on' && (
        <div
          style={{
            position: 'absolute',
            inset: '18%',
            border: '4px solid rgba(255,255,255,.7)',
            borderRadius: 16,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Camera error/start screen */}
      {cameraState !== 'on' && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            padding: 24,
            textAlign: 'center',
          }}
          role="region"
          aria-label="Caméra"
        >
          <button
            type="button"
            onClick={() => setCameraState('starting')}
            disabled={cameraState === 'starting'}
            aria-label={
              cameraState === 'starting'
                ? 'Ouverture de la caméra en cours'
                : 'Démarrer la caméra'
            }
            aria-busy={cameraState === 'starting'}
            style={{
              padding: '16px 24px',
              borderRadius: 12,
              background: '#0d9488',
              color: '#fff',
              border: 0,
              fontSize: 18,
              fontWeight: 600,
              cursor: cameraState === 'starting' ? 'wait' : 'pointer',
              opacity: cameraState === 'starting' ? 0.7 : 1,
            }}
          >
            {cameraState === 'starting' ? 'Ouverture…' : 'Démarrer la caméra'}
          </button>
          {cameraState === 'error' && (
            <p style={{ fontSize: 14, color: '#fcd34d' }} role="alert">
              Caméra inaccessible : autorisez-la dans le navigateur (le site
              doit être en https), ou saisissez le code ci-dessous.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default ScannerCamera;
